/**
 * Which hub devices are access devices (a garage door, a gate).
 *
 * The app's own surfaces already file the control under the covers' category
 * (`utils/controlTypes.ts` — `garage_door: { category: 'Covers' }`, and the
 * widget layer maps it to 'cover'), which is why a garage door was offered the
 * SHADE families in its 3D-model section. The library needs the distinction the
 * other way round: a door is not a blind, and a blind is not a door.
 *
 * The words are matched against the WORDS a name is built from, never as a bare
 * substring — the lesson `shadeDevices.ts` records (`roller` read out of
 * `sauna_controller`). `door` alone is deliberately NOT a word here: `doorbell`
 * and `door_sensor` start with it and are not doors.
 */

/** The class ids that ARE an access device, exactly as the hub spells them. */
export const ACCESS_CLASS_IDS = ['garage_door', 'garage_gate', 'gate', 'barrier', 'driveway_gate'] as const;

/** Word prefixes that mark an access device in the fields a hub names controls in. */
const ACCESS_WORD_HINTS = ['garage', 'gate', 'barrier'];

/** The fields a device names its control in, as the hubs spell them. */
export interface AccessCandidate {
  classId?: string | null;
  deviceType?: string | null;
  controlType?: string | null;
  deviceControl?: string | null;
}

function words(value: string | null | undefined): string[] {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word !== '');
}

function declaresAccessHint(value: string | null | undefined): boolean {
  return words(value).some((word) => ACCESS_WORD_HINTS.some((hint) => word.startsWith(hint)));
}

/**
 * True when the device is a garage door / gate — the class id first (that is
 * what `POST /api/devices {classId}` writes and what the hub's own registry
 * keys on), then the control words for the hubs that only carry a type.
 */
export function isAccessDevice(device: AccessCandidate | null | undefined): boolean {
  if (!device) return false;
  const classId = (device.classId ?? '').trim().toLowerCase();
  if (classId !== '' && (ACCESS_CLASS_IDS as readonly string[]).includes(classId)) return true;
  return (
    declaresAccessHint(device.deviceType) ||
    declaresAccessHint(device.controlType ?? device.deviceControl)
  );
}
